import { useEffect, useMemo, useState } from 'react';

import type { SatelliteCatalogState } from '../data/useSatelliteCatalog';
import { applySatelliteFilter } from '../filters/satelliteFilterEngine';
import { useStore } from '../store';
import { SatellitePanel } from '../details/SatellitePanel';
import { ISS_NORAD_ID } from './satelliteGlobeConstants';
import { SatelliteGlobeScene } from './SatelliteGlobeScene';

interface Props {
  catalog: SatelliteCatalogState;
}

function withPinnedSatellites(
  filtered: ReturnType<typeof applySatelliteFilter>,
  all: ReturnType<typeof applySatelliteFilter>,
  selectedSatId: string | null,
): ReturnType<typeof applySatelliteFilter> {
  const ids = new Set(filtered.map((s) => s.noradId));
  const extras = [];
  if (selectedSatId && !ids.has(selectedSatId)) {
    const selected = all.find((s) => s.noradId === selectedSatId);
    if (selected) extras.push(selected);
  }
  if (!ids.has(ISS_NORAD_ID) && !extras.some((s) => s.noradId === ISS_NORAD_ID)) {
    const iss = all.find((s) => s.noradId === ISS_NORAD_ID);
    if (iss) extras.push(iss);
  }
  return extras.length ? [...filtered, ...extras] : filtered;
}

export function SatelliteGlobeView({ catalog }: Props) {
  const setAppMode = useStore((s) => s.setAppMode);
  const satelliteFilter = useStore((s) => s.satelliteFilter);
  const selectedSatId = useStore((s) => s.selectedSatId);
  const setSelectedSat = useStore((s) => s.setSelectedSat);
  const followSat = useStore((s) => s.followSat);
  const focusSatId = useStore((s) => s.focusSatId);
  const focusSatSeq = useStore((s) => s.focusSatSeq);

  const filtered = useMemo(() => {
    const base = applySatelliteFilter(catalog.satellites, satelliteFilter);
    return withPinnedSatellites(base, catalog.satellites, selectedSatId);
  }, [catalog.satellites, satelliteFilter, selectedSatId]);

  const selectedSat = useMemo(() => {
    if (!selectedSatId) return null;
    return catalog.satellites.find((s) => s.noradId === selectedSatId) ?? null;
  }, [catalog.satellites, selectedSatId]);

  const [orbitPoints, setOrbitPoints] = useState<{
    noradId: string;
    points: Array<[number, number, number]>;
  } | null>(null);

  useEffect(() => {
    if (!selectedSatId) {
      setOrbitPoints(null);
      return;
    }
    let cancelled = false;
    catalog.fetchOrbit(selectedSatId).then((points) => {
      if (!cancelled) setOrbitPoints({ noradId: selectedSatId, points });
    });
    return () => {
      cancelled = true;
    };
  }, [catalog, selectedSatId]);

  return (
    <div className="absolute inset-0 z-0 bg-[#020617]">
      <SatelliteGlobeScene
        satellites={filtered}
        selectedNoradId={selectedSatId}
        followSat={followSat}
        focusNoradId={focusSatId}
        focusSeq={focusSatSeq}
        orbitPoints={orbitPoints}
        onSelect={setSelectedSat}
      />

      <div
        className="pointer-events-none absolute inset-x-0 bottom-0 z-10 flex flex-col gap-2 px-3 pb-3"
        style={{ paddingBottom: 'calc(var(--safe-bottom) + 0.75rem)' }}
      >
        <div className="pointer-events-auto flex items-center justify-between gap-2">
          <button
            type="button"
            onClick={() => setAppMode('map')}
            className="rounded-lg bg-bg-panel/90 px-3 py-2 text-sm font-medium text-slate-100 shadow ring-1 ring-bg-border backdrop-blur"
          >
            ← Map
          </button>
          <div className="rounded-lg bg-bg-panel/90 px-3 py-2 font-mono text-xs text-slate-300 shadow ring-1 ring-bg-border backdrop-blur">
            {catalog.loading
              ? 'loading satellites…'
              : catalog.error
                ? `· ${catalog.error}`
                : `${filtered.length.toLocaleString()} satellites`}
            {catalog.refreshing && !catalog.loading ? (
              <span className="text-slate-500"> · updating</span>
            ) : null}
            {catalog.error ? (
              <button type="button" className="ml-1 underline" onClick={() => catalog.refresh()}>
                retry
              </button>
            ) : null}
          </div>
        </div>
        {!selectedSatId ? (
          <div className="pointer-events-none text-center text-xs text-slate-500">Tap a satellite to select</div>
        ) : null}
      </div>

      <div
        className="pointer-events-none absolute bottom-2 left-2 z-10 rounded-md bg-bg-panel/80 px-2 py-1 text-[10px] text-slate-500 shadow ring-1 ring-bg-border backdrop-blur"
        style={{ paddingBottom: 'var(--safe-bottom)' }}
      >
        Orbital data ©{' '}
        <a
          href="https://celestrak.org"
          target="_blank"
          rel="noopener noreferrer"
          className="pointer-events-auto text-accent hover:underline"
        >
          CelesTrak
        </a>
      </div>

      <SatellitePanel satellite={selectedSat} selectedSatId={selectedSatId} />
    </div>
  );
}
